import { InventoryHoldArea } from "../../constants"

import type { FileTypeToChipMap } from "@/components/Tables/Aeros/hooks/useFileColumns"
import {
  FieldGroupEnum,
  TransferReason,
  TransferStatus,
  TransferType,
  WarehousePalletDestinationArea,
} from "@/graphql/codegen/graphql"
import colors from "@/styles/colors"
import type { Option } from "@/types/form-fields"

export { TransferReason, TransferStatus, TransferType }

export const HOLD_AREA_TO_DESTINATION_AREA: Partial<Record<InventoryHoldArea, WarehousePalletDestinationArea>> = {
  [InventoryHoldArea.InboundHold]: WarehousePalletDestinationArea.InboundHold,
  [InventoryHoldArea.OutboundHold]: WarehousePalletDestinationArea.OutboundHold,
  [InventoryHoldArea.OffSpecArea]: WarehousePalletDestinationArea.OffSpec,
}

export const TRANSFER_TYPE_TO_LABEL: Record<TransferType, string> = {
  [TransferType.IntraWarehouse]: "Intra Warehouse Transfer",
  [TransferType.InterWarehouse]: "Inter Warehouse Transfer",
} as const

export const TRANSFER_TYPE_OPTIONS: Option<TransferType>[] = [
  { value: TransferType.IntraWarehouse, label: TRANSFER_TYPE_TO_LABEL[TransferType.IntraWarehouse] },
  { value: TransferType.InterWarehouse, label: TRANSFER_TYPE_TO_LABEL[TransferType.InterWarehouse] },
]

export const TRANSFER_TYPE_TO_SHORT_LABEL: Record<TransferType, string> = {
  [TransferType.IntraWarehouse]: "Intra",
  [TransferType.InterWarehouse]: "Inter",
} as const

export const TRANSFER_STATUS_TO_LABEL: Record<TransferStatus, string> = {
  [TransferStatus.Draft]: "Draft",
  [TransferStatus.PendingPickAndPutaway]: "Pending Pick and Putaway",
  [TransferStatus.PendingPick]: "Pending Pick",
  [TransferStatus.PendingShip]: "Pending Ship",
  [TransferStatus.InTransit]: "In Transit",
  [TransferStatus.Received]: "Received",
  [TransferStatus.Completed]: "Completed",
  [TransferStatus.Cancelled]: "Cancelled",
} as const

export const TRANSFER_FILTERABLE_STATUSES: TransferStatus[] = Object.values(TransferStatus).filter(
  (status) => status !== TransferStatus.Cancelled
)

// TODO(AK-6818): Replace with the generated enum once the backend exposes AS400 sync status filtering.
export enum TransferSyncStatusFilter {
  PendingSync = "PENDING_SYNC",
  PartiallyFailed = "PARTIALLY_FAILED",
  FailedCreation = "FAILED_CREATION",
}

export type TransferStatusFilterValue = Exclude<TransferStatus, TransferStatus.Cancelled> | TransferSyncStatusFilter

export enum TransferProductCostCompleted {
  All = "ALL",
  Yes = "YES",
  No = "NO",
}

export enum TransferActionType {
  Edit = "EDIT",
  LpnEdit = "LPN_EDIT",
  LaidDownCostEdit = "LAID_DOWN_COST_EDIT",
  WorkOrderConfirm = "WORK_ORDER_CONFIRM",
  WorkOrderPrint = "WORK_ORDER_PRINT",
  PickTicketPrint = "PICK_TICKET_PRINT",
  Cancel = "CANCEL",
  PalletLabelPrint = "PALLET_LABEL_PRINT",
  DocumentsView = "DOCUMENTS_VIEW",
  ConfirmReceived = "CONFIRM_RECEIVED",
}

export const TRANSFER_COST_DECIMAL_PRECISION = 4

export const TRANSFER_REASON_OPTIONS: Option<string>[] = [
  { value: TransferReason.Replenishment, label: "Replenishment" },
  { value: TransferReason.QualityIssue, label: "Quality Issue" },
  { value: TransferReason.LocationOptimization, label: "Location Optimization" },
  { value: TransferReason.InventoryRebalancing, label: "Inventory Rebalancing" },
  { value: TransferReason.DamagedGoods, label: "Damaged Goods" },
  { value: TransferReason.ProductConsolidation, label: "Product Consolidation" },
]

export const TRANSFER_DOCUMENT_TYPE_TO_CHIP: FileTypeToChipMap = {
  [FieldGroupEnum.Transfer]: { label: "Transfer", color: colors.primary["200"] },
}

// TODO(AK-6103): Replace with the generated enum once the backend exposes variance reasons.
export enum TransferVarianceReason {
  None = "NONE",
  ProductDamaged = "PRODUCT_DAMAGED",
  ProductMissing = "PRODUCT_MISSING",
  ShortShipped = "SHORT_SHIPPED",
  OverShipped = "OVER_SHIPPED",
  IncorrectProduct = "INCORRECT_PRODUCT",
  PackagingDamaged = "PACKAGING_DAMAGED",
  LotBatchMismatch = "LOT_BATCH_MISMATCH",
  QuantityCountError = "QUANTITY_COUNT_ERROR",
  Other = "OTHER",
}

export const TRANSFER_VARIANCE_REASON_TO_LABEL: Record<TransferVarianceReason, string> = {
  [TransferVarianceReason.None]: "None",
  [TransferVarianceReason.ProductDamaged]: "Product Damaged",
  [TransferVarianceReason.ProductMissing]: "Product Missing",
  [TransferVarianceReason.ShortShipped]: "Short Shipped",
  [TransferVarianceReason.OverShipped]: "Over Shipped",
  [TransferVarianceReason.IncorrectProduct]: "Incorrect Product",
  [TransferVarianceReason.PackagingDamaged]: "Packaging Damaged",
  [TransferVarianceReason.LotBatchMismatch]: "Lot / Batch Mismatch",
  [TransferVarianceReason.QuantityCountError]: "Quantity Count Error",
  [TransferVarianceReason.Other]: "Other",
} as const
